import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  FormController,
  MuiBox,
  MuiChipWithListView,
  MuiDateField,
  MuiGrid,
  MuiLink,
  MuiMultiSelect,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  TableHeaderSearchIcon,
} from "../../../../../components";
import MuiEllipseLabel from "../../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  STATUS_BADGE_TYPE,
  StatusBadge,
} from "../../../../../components/statusBadge/StatusBadge";
import {
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
  PAGE_IDS,
  PAGE_URLS,
} from "../../../../../helpers/constants";
import {
  DATE_FORMAT,
  EMPTY_VALUE_DASH,
} from "../../../../../helpers/constants/common";
import { getDateFormatByCountry } from "../../../../../helpers/constants/countryDateFormat";
import { LabelValue } from "../../../../../helpers/types/common";
import {
  arrayOfObjectsIncludesValue,
  buildActionMenuItems,
  checkFilterMatch,
  compareDates,
  convertToUserFormat,
  getUniqueCountriesFromData,
  getValueOrBlank,
  isAllParticipants,
  isDateInRange,
  isEmpty,
  isNotEmpty,
  replaceHashWithValue,
  stringIncludesArrayOfStringsValue,
} from "../../../../../helpers/utils";
import { useMultiSelectARIALabels } from "../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../redux/slices/appState/appStateSelector";
import { updateProgramCreatedDate } from "../../../../../redux/slices/program/programSlice";
import { ParticipantAddActivity } from "../../../../createProgram/components/createProgramTabs/tabs/participant/components/participantAddActivity/ParticipantAddActivity";
import { activityTimelineSiteCoreKeys } from "../../../../createProgram/siteCore/SiteCoreKeys";
import {
  getCoachNamesWithType,
  getInfoMessageForAssignedCoaches,
} from "../../../helper";
import {
  ParticipantSearchResultForm,
  ParticipantSearchResultRowValue,
  ParticipantsSearchFormKey,
  SearchParticipantsResultFormKey,
  UniqueParticipantSearchResultFiltersTypes,
} from "../../../modelsAndSchemas/Models";
import { searchParticipantsResultsSectionSiteCoreKeys } from "../../../sitecore/SiteCoreKeys";
import { ParticipantsSearchResultWrapper } from "./ParticipantsSearchResult.style";

const pageId = PAGE_IDS.SEARCH_PARTICIPANTS_RESULTS;

interface ParticipantsSearchResultProps {
  rows: ParticipantSearchResultRowValue[];
  isSearch: boolean;
}

export const ParticipantsSearchResult: FC<ParticipantsSearchResultProps> = ({
  rows,
  isSearch,
}) => {
  const {
    useFetchLabel,
    fetchComponentTitle,
    fetchValueByKeyOfDropdown,
    fetchLabel,
    commonLabels,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const dispatch = useDispatch();

  const {
    searchParticipantsResultsSectionTitle,
    myCaseloadsSectionHeader,
    allParticipantsSectionHeader,
    participantNameLabel,
    programKeyLabel,
    nextSessionEventLabel,
    nextViewLabel,
    assignedCoachesLabel,
    clientNameLabel,
    programStatusLabel,
    participantEmailLabel,
    participantMobileNumberLabel,
    projectManagerLabel,
    countryLabel,
    programStartMonthYearLabel,
    programEndDateLabel,
    actionLabel,
    programStatusList,
    addActivityLabel,
  } = searchParticipantsResultsSectionSiteCoreKeys;
  const getLabel = useFetchLabel(searchParticipantsResultsSectionTitle);
  const multiSelectARIALabels = useMultiSelectARIALabels();

  const tableRef = React.useRef<{
    resetPagination: (shouldResetRowsPerPage?: boolean) => void;
  }>(null);
  const [filteredResults, setFilteredResults] = useState([...rows]);
  const [uniqueCountries, setUniqueCountries] = useState<LabelValue[]>([]);

  const [uniqueFilters, setUniqueFilters] =
    useState<UniqueParticipantSearchResultFiltersTypes>(() => ({
      programStatus: [],
    }));

  // updated sitecore data
  const [updatedRows, setUpdatedRows] = useState<
    ParticipantSearchResultRowValue[]
  >([]);
  const currentParticipantRef = useRef<ParticipantSearchResultRowValue>();
  const [isOpenActivity, setIsOpenActivity] = useState(false);

  const navigate = useNavigate();
  const { hasModulePermission } = usePermissions();

  const resetPagination = (shouldResetRowsPerPage: boolean = false) => {
    if (tableRef.current) {
      tableRef.current.resetPagination(shouldResetRowsPerPage);
    }
  };

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const { getValues: getParentFormValues } = useFormContext();

  const { control, setValue, getValues, reset } =
    useForm<ParticipantSearchResultForm>({
      mode: "all",
      defaultValues: {
        [SearchParticipantsResultFormKey.participantName]: "",
        [SearchParticipantsResultFormKey.keyNumber]: "",
        [SearchParticipantsResultFormKey.nextViewName]: "",
        [SearchParticipantsResultFormKey.nextSessionEvent]: "",
        [SearchParticipantsResultFormKey.companyName]: "",
        [SearchParticipantsResultFormKey.programStatus]: "",
        [SearchParticipantsResultFormKey.participantEmail]: "",
        [SearchParticipantsResultFormKey.coaches]: "",
        [SearchParticipantsResultFormKey.participantMobileNumber]: "",
        [SearchParticipantsResultFormKey.projectManager]: "",
        [SearchParticipantsResultFormKey.countryIds]: [],
        [SearchParticipantsResultFormKey.programStartDate]: "",
        [SearchParticipantsResultFormKey.programEndDate]: "",
      },
    });
  const formValues = useWatch({ control });
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  useEffect(() => {
    resetPagination(true);
    const rowsData = [...rows];

    const uniqueCountriesData = getUniqueCountriesFromData(rowsData);

    setUniqueCountries(uniqueCountriesData);
    reset();
  }, [rows]);

  const getUniqueFiltersForParticipants = (
    data: ParticipantSearchResultRowValue[],
    key: string,
  ): LabelValue[] => {
    return Array.from(
      new Set(
        data
          .filter((item) => item[key]) // Filter out null or undefined values
          .map((item) => item[key]) // Extract values for the given key
          .reduce((acc, val) => acc.concat(val), []), // Flatten the array if values are arrays
      ),
    ).map((value) => ({ value: value as string, label: value as string })); // Format as value-label pairs
  };

  useEffect(() => {
    if (!siteCoreLoading) {
      const localRowsData = [...rows];

      localRowsData.forEach((data) => {
        data.coachNamesArray = getCoachNamesWithType(
          data.coaches,
          getValueOrBlank(loggedInUser?.id),
        );

        data.statusKey = data.programStatus;

        data.nextViewName = data.nextView.name;

        data.programStatus = fetchValueByKeyOfDropdown(
          searchParticipantsResultsSectionTitle,
          programStatusList,
          data.programStatus,
        );
      });
      setUpdatedRows(localRowsData);
      setFilteredResults([...localRowsData]);

      setUniqueFilters((prevFilters) => ({
        ...prevFilters,
        [SearchParticipantsResultFormKey.programStatus]:
          getUniqueFiltersForParticipants(
            localRowsData,
            SearchParticipantsResultFormKey.programStatus,
          ),
      }));
    }
  }, [rows, siteCoreLoading]);

  useEffect(() => {
    if (isNotEmpty(updatedRows)) {
      const {
        participantName,
        keyNumber,
        nextViewName,
        nextSessionEvent,
        countryIds,
        companyName,
        coaches,
        programStatus,
        participantEmail,
        participantMobileNumber,
        projectManager,
        programStartDate,
        programEndDate,
      } = getValues();

      const myData = updatedRows.filter((item) => {
        const keyNumberMatch = checkFilterMatch(keyNumber, item.keyNumber);
        const nextViewNameMatch = checkFilterMatch(
          nextViewName,
          item.nextView.name,
        );
        const nextSessionEventMatch = checkFilterMatch(
          nextSessionEvent,
          item.nextSessionEvent,
        );

        // for display purpose its ClientName field.
        const companyNameMatch = checkFilterMatch(
          companyName,
          item.companyName,
        );

        const isCoachNamesMatch = arrayOfObjectsIncludesValue(
          coaches,
          item.coachNamesArray,
          "label",
        );

        const participantNameMatch = checkFilterMatch(
          participantName,
          getValueOrBlank(item.participantName),
        );

        const participantMobileNumberMatch = checkFilterMatch(
          participantMobileNumber,
          getValueOrBlank(item.participantMobileNumber),
        );

        const participantEmailMatch = checkFilterMatch(
          participantEmail,
          getValueOrBlank(item.participantEmail),
        );

        const projectManagerMatch = checkFilterMatch(
          projectManager,
          getValueOrBlank(item.projectManager),
        );

        const countryMatch = stringIncludesArrayOfStringsValue(
          countryIds,
          item.country,
        );

        const statusMatch =
          programStatus === "" ||
          item.programStatus?.toLowerCase() === programStatus?.toLowerCase();

        let isProgramStartDateMatch = true;

        if (programStartDate) {
          if (!item?.programStartDate) {
            isProgramStartDateMatch = false;
          } else {
            const selectedDate = dayjs(programStartDate, userCountryDateFormat);
            const startOfSelectedDateMonth = selectedDate.startOf("month");
            const endOfSelectedDateMonth = selectedDate.endOf("month");

            const itemDate = dayjs(item?.programStartDate);

            isProgramStartDateMatch = isDateInRange(
              itemDate,
              startOfSelectedDateMonth,
              endOfSelectedDateMonth,
            );
          }
        }
        let isProgramEndDateMatch = compareDates(
          programEndDate,
          item?.programEndDate ?? "",
          getValueOrBlank(userCountryDateFormat),
        );

        return (
          keyNumberMatch &&
          nextViewNameMatch &&
          nextSessionEventMatch &&
          companyNameMatch &&
          isCoachNamesMatch &&
          statusMatch &&
          isProgramStartDateMatch &&
          isProgramEndDateMatch &&
          countryMatch &&
          participantNameMatch &&
          participantMobileNumberMatch &&
          participantEmailMatch &&
          projectManagerMatch
        );
      });
      setFilteredResults([...myData]);
      resetPagination();
    }
  }, [formValues]);

  const isAllParticipantsFlag = useMemo(
    () =>
      isAllParticipants(
        getParentFormValues(ParticipantsSearchFormKey.searchType),
      ),
    [getParentFormValues(ParticipantsSearchFormKey.searchType)],
  );

  const handleEditItemClick = (row?: ParticipantSearchResultRowValue) => {
    navigate(`${PAGE_URLS.COACH_PORTAL_EDIT_PARTICIPANT}/${row?.id}`);
  };

  const handleViewItemClick = (row?: ParticipantSearchResultRowValue) => {
    navigate(`${PAGE_URLS.COACH_PORTAL_VIEW_PARTICIPANT}/${row?.id}`);
  };

  const handleAddActivityClick = (row: ParticipantSearchResultRowValue) => {
    if (isEmpty(row.id)) return;
    dispatch(updateProgramCreatedDate(row?.createdDate));
    currentParticipantRef.current = row;
    setIsOpenActivity(true);
  };

  const getActivityHeaderLabel = () => {
    const activityTitle = fetchLabel(
      activityTimelineSiteCoreKeys.activityTimeline,
      activityTimelineSiteCoreKeys.addActivityParticipantNamePopupHeader,
    );

    return replaceHashWithValue(
      activityTitle,
      getValueOrBlank(currentParticipantRef.current?.participantName),
    );
  };

  const handleActivityModalClose = () => setIsOpenActivity(false);

  const columns = [
    {
      id: SearchParticipantsResultFormKey.participantName,
      label: getLabel(participantNameLabel),
      isEmptyValue: true,
      isSortVisible: true,
      className: "cell-participant-name",
      render: (row: any) => {
        if (!row.participantName) return null;
        else {
          return hasModulePermission(MODULE_PERMISSIONS.COACH_PORTAL_VIEW) ? (
            <MuiLink
              isOpenNewTab
              href={`${PAGE_URLS.COACH_PORTAL_VIEW_PARTICIPANT}/${row.id}`}
              id={`${pageId}-manage-participant`}
              data-testid="manage-participant-link"
            >
              <MuiBox data-testid="participant-name-link">
                <MuiEllipseLabel
                  data-testid="participant-name-link"
                  labelTypoClassName="Link participant-name-link"
                  text={row.participantName}
                  labelTypoVariant="h3"
                  placement="bottom-start"
                  pointerCursor
                />
              </MuiBox>
            </MuiLink>
          ) : (
            <MuiBox>
              <MuiTypography variant="h3" className="">
                {row.participantName}
              </MuiTypography>
            </MuiBox>
          );
        }
      },
    },
    {
      id: SearchParticipantsResultFormKey.keyNumber,
      label: getLabel(programKeyLabel),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: SearchParticipantsResultFormKey.nextViewName,
      label: getLabel(nextViewLabel),
      isSortVisible: true,
      isEmptyValue: true,
    },
    ...(!isAllParticipantsFlag
      ? [
          {
            id: SearchParticipantsResultFormKey.nextSessionEvent,
            label: getLabel(nextSessionEventLabel),
            isSortVisible: true,
            isEmptyValue: true,
          },
        ]
      : []),
    {
      id: SearchParticipantsResultFormKey.coaches,
      label: getLabel(assignedCoachesLabel),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) =>
        isNotEmpty(row.coachNamesArray) ? (
          <MuiBox className="cell-assigned-coaches">
            <MuiChipWithListView
              data={row.coachNamesArray}
              headerLabel={getLabel(assignedCoachesLabel)}
              gridRef={React.createRef<HTMLElement>()}
              countToWrap={1}
              infoMessage={getInfoMessageForAssignedCoaches(
                row.coachNamesArray,
              )}
              removeChipNameARIALabel={commonARIALabel.removeChipName}
              closePopoverARIALabel={commonARIALabel.closePopover}
              showMoreARIALabel={commonARIALabel.showMoreDetails}
            />
          </MuiBox>
        ) : (
          EMPTY_VALUE_DASH
        ),
    },
    {
      id: SearchParticipantsResultFormKey.companyName,
      label: getLabel(clientNameLabel),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: SearchParticipantsResultFormKey.programStatus,
      label: getLabel(programStatusLabel),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <StatusBadge status={row.statusKey} label={row.programStatus} />
      ),
    },
    {
      id: SearchParticipantsResultFormKey.participantEmail,
      label: getLabel(participantEmailLabel),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: SearchParticipantsResultFormKey.participantMobileNumber,
      label: getLabel(participantMobileNumberLabel),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: SearchParticipantsResultFormKey.projectManager,
      label: getLabel(projectManagerLabel),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: SearchParticipantsResultFormKey.countryName,
      label: getLabel(countryLabel),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-country",
    },
    {
      id: SearchParticipantsResultFormKey.programStartDate,
      label: getLabel(programStartMonthYearLabel),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {!isEmpty(row.programStartDate)
              ? dayjs(row.programStartDate).format(DATE_FORMAT.MMMM_YYYY)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: SearchParticipantsResultFormKey.programEndDate,
      label: getLabel(programEndDateLabel),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {isNotEmpty(row.programEndDate)
              ? convertToUserFormat(row.programEndDate, userCountryDateFormat)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: "acton",
      label: getLabel(actionLabel),
      render: (row: RowProps) => {
        const isProgramClosedStatus =
          STATUS_BADGE_TYPE.closed === row.statusKey;

        const editAction = {
          permissionKey: MODULE_PERMISSIONS.COACH_PORTAL_EDIT,
          create: () => ({
            onClick: () =>
              handleEditItemClick(row as ParticipantSearchResultRowValue),
            dataTestId: `edit`,
            label: commonLabels.edit,
            ariaLabel: commonLabels.edit,
          }),
        };

        const actions = [
          {
            permissionKey: MODULE_PERMISSIONS.COACH_PORTAL_VIEW,
            create: () => ({
              onClick: () =>
                handleViewItemClick(row as ParticipantSearchResultRowValue),
              dataTestId: `view`,
              label: commonLabels.view,
              ariaLabel: commonLabels.view,
            }),
          },
          {
            permissionKey: MODULE_PERMISSIONS.COACH_PORTAL_EDIT,
            create: () => ({
              onClick: () =>
                handleAddActivityClick(row as ParticipantSearchResultRowValue),
              dataTestId: `activity`,
              label: getLabel(addActivityLabel),
              ariaLabel: getLabel(addActivityLabel),
            }),
          },
        ];

        if (!isProgramClosedStatus) {
          actions.splice(1, 0, editAction); // add edit action at index 1 if program is not closed
        }

        const menuItems = buildActionMenuItems(
          row,
          actions,
          hasModulePermission,
        );

        return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
      },
    },
  ];

  const handleMuiDateChange = (key: string, newValue: dayjs.Dayjs | null) => {
    let dateValue = "";
    if (newValue?.isValid()) {
      dateValue = newValue.format(userCountryDateFormat);
    }
    setValue(key, dateValue);
  };

  const renderSearchIcon = () => <TableHeaderSearchIcon />;

  const tableHeaderForm = [
    <FormController
      control={control}
      name={SearchParticipantsResultFormKey.participantName}
      key={SearchParticipantsResultFormKey.participantName}
    >
      <MuiTextField
        id={`${pageId}-${SearchParticipantsResultFormKey.participantName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchParticipantsResultFormKey.keyNumber}
      key={SearchParticipantsResultFormKey.keyNumber}
    >
      <MuiTextField
        id={`${pageId}-${SearchParticipantsResultFormKey.keyNumber}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchParticipantsResultFormKey.nextViewName}
      key={SearchParticipantsResultFormKey.nextViewName}
    >
      <MuiTextField
        id={`${pageId}-${SearchParticipantsResultFormKey.nextViewName}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    ...(!isAllParticipantsFlag
      ? [
          <FormController
            control={control}
            name={SearchParticipantsResultFormKey.nextSessionEvent}
            key={SearchParticipantsResultFormKey.nextSessionEvent}
          >
            <MuiTextField
              id={`${pageId}-${SearchParticipantsResultFormKey.nextSessionEvent}`}
              startAdornment={renderSearchIcon()}
              maxLength={MAX_LENGTH_50}
              placeholder={commonLabels.search}
              showCloseIcon
              shouldTrimSpaces={false}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>,
        ]
      : []),
    <FormController
      control={control}
      name={SearchParticipantsResultFormKey.coaches}
      key={SearchParticipantsResultFormKey.coaches}
    >
      <MuiTextField
        id={`${pageId}-${SearchParticipantsResultFormKey.coaches}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchParticipantsResultFormKey.companyName}
      key={SearchParticipantsResultFormKey.companyName}
    >
      <MuiTextField
        id={`${pageId}-${SearchParticipantsResultFormKey.companyName}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchParticipantsResultFormKey.programStatus}
      key={SearchParticipantsResultFormKey.programStatus}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.programStatus}
        visibleDefaultSelectOption
        value=""
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${SearchParticipantsResultFormKey.programStatus}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchParticipantsResultFormKey.participantEmail}
      key={SearchParticipantsResultFormKey.participantEmail}
    >
      <MuiTextField
        id={`${pageId}-${SearchParticipantsResultFormKey.participantEmail}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchParticipantsResultFormKey.participantMobileNumber}
      key={SearchParticipantsResultFormKey.participantMobileNumber}
    >
      <MuiTextField
        id={`${pageId}-${SearchParticipantsResultFormKey.participantMobileNumber}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchParticipantsResultFormKey.projectManager}
      key={SearchParticipantsResultFormKey.projectManager}
    >
      <MuiTextField
        id={`${pageId}-${SearchParticipantsResultFormKey.projectManager}`}
        startAdornment={renderSearchIcon()}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchParticipantsResultFormKey.countryIds}
      key={SearchParticipantsResultFormKey.countryIds}
    >
      <MuiMultiSelect
        label=""
        disabled={false}
        showSearch={true}
        showChip={false}
        textFieldWidth={0}
        options={uniqueCountries}
        id={`${pageId}-${SearchParticipantsResultFormKey.countryIds}`}
        selectPlaceHolder={commonLabels.selectPlaceHolder}
        searchPlaceHolder={commonLabels.search}
        selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
        doneText={commonLabels.done}
        allARIAProps={multiSelectARIALabels}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchParticipantsResultFormKey.programStartDate}
      key={SearchParticipantsResultFormKey.programStartDate}
    >
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        localeText={{
          fieldMonthPlaceholder: () => commonLabels.monthPlaceHolder,
          fieldYearPlaceholder: () => commonLabels.yearPlaceHolder,
        }}
      >
        <MuiDateField
          value={
            getValues().programStartDate !== ""
              ? dayjs(getValues().programStartDate, userCountryDateFormat)
              : null
          }
          format={DATE_FORMAT.MMM_YYYY_SLASH}
          views={["month", "year"]}
          clearable
          onChange={(newValue) =>
            handleMuiDateChange(
              SearchParticipantsResultFormKey.programStartDate,
              newValue,
            )
          }
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      control={control}
      name={SearchParticipantsResultFormKey.programEndDate}
      key={SearchParticipantsResultFormKey.programEndDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          value={
            getValues().programEndDate !== ""
              ? dayjs(getValues().programEndDate, userCountryDateFormat)
              : null
          }
          format={userCountryDateFormat}
          clearable
          onChange={(newValue) => {
            setValue(
              SearchParticipantsResultFormKey.programEndDate,
              newValue?.format(userCountryDateFormat) ?? "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <></>,
  ];

  const selectedRadioButtonTitle = useMemo(
    () =>
      isAllParticipantsFlag
        ? getLabel(allParticipantsSectionHeader)
        : getLabel(myCaseloadsSectionHeader),
    [isAllParticipantsFlag, getLabel],
  );

  return (
    <ParticipantsSearchResultWrapper data-testid="participants-search-result-wrapper">
      <MuiGrid container>
        <MuiGrid size={{ xs: 12 }} className="form-header">
          <MuiTypography variant="h2SemiBold" className="form-header-left">
            {isSearch
              ? fetchComponentTitle(searchParticipantsResultsSectionTitle)
              : selectedRadioButtonTitle}
          </MuiTypography>
          {isNotEmpty(rows) && (
            <MuiTypography variant="h3SemiBold" className="selected-count">
              {rows.length}
            </MuiTypography>
          )}
        </MuiGrid>
        <MuiGrid container>
          <MuiGrid size={{ xs: 12 }} className="header-right">
            <MuiTable
              ref={tableRef}
              pageId={pageId}
              tableHeaderForm={!siteCoreLoading ? tableHeaderForm : []}
              columns={columns}
              tableResults={[...filteredResults]}
              noDataInApi={rows.length === 0 && !isSearch}
              emptyMessage={commonLabels.applyNecessaryFiltersMessage}
              noResultMessage={commonLabels.noResult}
              priorityResults={[]}
              isLastColumnSticky
              isShowDashIfValueEmpty
            />
          </MuiGrid>
        </MuiGrid>
        {isOpenActivity && (
          <ParticipantAddActivity
            show={isOpenActivity}
            handleCloseModal={handleActivityModalClose}
            pageId={pageId}
            programId={getValueOrBlank(currentParticipantRef.current?.id)}
            getListOfActivitiesApi={handleActivityModalClose} //  Need to verify functionality here
            isEditActivity={false}
            customHeaderLabel={getActivityHeaderLabel()}
          />
        )}
      </MuiGrid>
    </ParticipantsSearchResultWrapper>
  );
};
